<script setup lang="ts">
/**
 * KOMPRES FOTO — halaman induk alat gratis.
 *
 * Ini PINTU MASUK SEO terbesar situs. Salinan & metadata karenanya tidak boleh
 * diubah sedikit pun, dan memang tidak diubah di sini: judul, deskripsi,
 * seluruh butir FAQ, dan ketiga blok JSON-LD (SoftwareApplication, FAQPage,
 * BreadcrumbList) dibawa PERSIS dari versi lama (kini di /legacy/kompres-foto).
 * Yang berubah hanya cara isinya ditampilkan.
 *
 * `path` canonical tetap '/kompres-foto' — alamat yang sama seperti sebelum
 * pergantian desain, sehingga peringkat yang sudah ada tidak terputus.
 *
 * Yang SENGAJA tidak dipakai di halaman ini: useGerak(). Halaman SEO harus
 * ringan, dan animasi muncul-saat-gulir membayar JS untuk keuntungan yang tidak
 * dirasakan orang yang datang untuk mengecilkan satu foto lalu pergi.
 */
import type { Faq } from '~/composables/useSeo'

/**
 * Layout `ringan`, bukan `default`. Layout utama membungkus isinya dengan
 * <UApp> sehingga setiap halaman menanggung runtime Nuxt UI lewat layout —
 * dan halaman ini tidak memakai satu pun perilaku library itu. Tampilannya
 * tetap tema v3 yang sama persis; lihat catatan lengkap di layouts/ringan.vue.
 */
definePageMeta({ layout: 'ringan' })

const situs = useSitusUrl()

const faq: Faq[] = [
  {
    tanya: 'Kenapa hasilnya tidak persis sama dengan target KB yang saya pilih?',
    jawab:
      'Kompresi JPEG dikendalikan lewat tingkat kualitas, bukan lewat ukuran berkas secara langsung. Alat ini mencari kualitas tertinggi yang hasilnya masih di bawah target, jadi angkanya mendekati target dari bawah — misalnya 186 KB untuk target 200 KB. Itu justru memberi jarak aman dari batas portal.'
  },
  {
    tanya: 'Apakah fotonya jadi buram?',
    jawab:
      'Tergantung seberapa jauh jarak antara ukuran asli dan target. Untuk pas foto, target 100 sampai 200 KB umumnya masih tajam. Bila target tidak bisa dicapai tanpa membuat berkas sulit dibaca, alat ini berhenti dan melaporkannya sebagai tidak tercapai beserta ukuran terbaik yang dicapai — kami tidak menghasilkan berkas buram diam-diam lalu menyebutnya berhasil.'
  },
  {
    tanya: 'Apakah foto saya disimpan di server?',
    jawab:
      'Tidak. Berkas diproses di memori lalu hasilnya langsung dikirim balik. Tidak ada salinan yang ditulis ke penyimpanan dan tidak ada akun yang perlu dibuat.'
  },
  {
    tanya: 'Format apa saja yang didukung?',
    jawab:
      'JPG, JPEG, PNG, dan WebP, dengan hasil berformat JPEG. Format HEIC atau HEIF dari iPhone belum didukung — buka fotonya di aplikasi Foto lalu simpan atau ekspor sebagai JPEG terlebih dahulu.'
  },
  {
    tanya: 'Bisakah saya memproses banyak foto sekaligus?',
    jawab:
      'Alat gratis ini sengaja dibatasi satu berkas per proses supaya tetap cepat dan tidak perlu antrean. Untuk memproses seluruh berkas pendaftaran sekaligus, masing-masing dengan target ukurannya sendiri, gunakan BerkasKilat.'
  }
]

pakaiSeo({
  judul: 'Kompres Foto ke Ukuran KB yang Kamu Tentukan — Gratis, Tanpa Akun',
  deskripsi:
    'Kompres foto JPG, PNG, atau WebP ke 100 KB, 200 KB, 500 KB, atau angka bebas. Preset ukuran SSCASN, SNPMB, dan PPDB. Gratis, tanpa akun, berkas tidak disimpan.',
  path: '/kompres-foto',
  jsonLd: [
    jsonLdAplikasi({
      nama: 'Kompres Foto ke Ukuran KB',
      deskripsi:
        'Alat daring gratis untuk menurunkan ukuran berkas foto ke target kilobyte tertentu, dengan preset ukuran untuk pendaftaran SSCASN, SNPMB, dan SPMB.',
      url: situs + '/kompres-foto',
      harga: 0
    }),
    jsonLdFaq(faq),
    jsonLdRemah(situs, [
      ['Beranda', '/'],
      ['Kompres Foto', '/kompres-foto']
    ])
  ]
})

const preset = [
  { ke: '/kompres-foto/100kb', label: '100 KB', konteks: 'Foto akun SNPMB (40–100 KB)' },
  { ke: '/kompres-foto/200kb', label: '200 KB', konteks: 'Pas foto, swafoto, KTP SSCASN' },
  { ke: '/kompres-foto/300kb', label: '300 KB', konteks: 'Foto profil SPMB / PPDB' },
  { ke: '/kompres-foto/500kb', label: '500 KB', konteks: 'Pindaian dokumen berwarna' }
]
</script>

<template>
  <div>
    <!-- HERO + ALAT -->
    <section class="relative overflow-hidden">
      <div class="ca-aura" aria-hidden="true" />

      <div class="relative mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 pt-8 sm:pt-12 pb-10">
        <RinganRemah :jejak="[['Beranda', '/'], ['Kompres Foto', '']]" />

        <div class="mt-5 grid gap-9 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:items-start">
          <div class="min-w-0">
            <RinganLencana tone="ok" icon="gift">Gratis selamanya</RinganLencana>

            <h1 class="ca-h1 mt-4 text-ink text-balance">
              Kompres foto ke ukuran KB yang kamu tentukan
            </h1>

            <p class="mt-4 text-lg text-muted leading-relaxed max-w-xl">
              Pilih berkas, tentukan target ukuran, selesai. Tidak perlu mendaftar, tidak ada
              antrean, dan berkas kamu tidak disimpan di server. Bila targetnya mustahil dicapai
              tanpa membuat foto sulit dibaca, kami mengatakannya apa adanya.
            </p>

          </div>

          <RinganKompresAlat jenis="foto" :target-awal="200" />
        </div>

        <!-- Sinyal kepercayaan diletakkan SELEBAR halaman, bukan di kolom kiri.
             Di dalam kolom kiri, empat butir harus berbagi ~530px dan tiap
             judulnya patah jadi tiga baris ("Tanpa / akun / Langsung pakai") —
             terbaca seperti tabel rusak, bukan sebagai jaminan. -->
        <div class="mt-10">
          <RinganBilahPercaya
            :butir="[
              { ikon: 'user-x', judul: 'Tanpa akun', detail: 'Langsung pakai' },
              { ikon: 'shield-check', judul: 'Tidak disimpan', detail: 'Diproses di memori server' },
              { ikon: 'zap', judul: 'Tanpa antrean', detail: 'Hasil langsung jadi' },
              { ikon: 'wallet', judul: 'Tanpa biaya', detail: 'Sekarang & seterusnya' }
            ]"
          />
        </div>
      </div>
    </section>

    <!-- PRESET PER INSTANSI -->
    <section class="ca-tunda py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganJudulBagian
          badge="Preset siap pakai"
          badge-ikon="target"
          judul="Butuh ukuran untuk instansi tertentu?"
          ringkas="Tiap portal pendaftaran punya batas ukurannya sendiri. Halaman di bawah menjelaskan ketentuan yang kami catat berikut sumber dan tahunnya."
        />

        <div class="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <NuxtLink v-for="p in preset" :key="p.ke" :to="p.ke" class="ca-focus rounded-card">
            <CaCard interaktif class="h-full">
              <p class="ca-num ca-h2 text-brand leading-none">{{ p.label }}</p>
              <p class="ca-caption text-muted mt-3 leading-relaxed">{{ p.konteks }}</p>
              <p class="ca-caption text-brand font-semibold mt-4 inline-flex items-center gap-1">
                Buka halamannya
                <RinganIkon nama="arrow-right" class="size-3.5" />
              </p>
            </CaCard>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- URAIAN -->
    <section class="ca-tunda py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganJudulBagian
          badge="Cara kerjanya"
          badge-ikon="settings-2"
          judul="Kenapa hampir semua orang perlu mengompres dulu"
        />

        <CaProsa class="mt-6 max-w-3xl">
          <p>
            Foto dari kamera ponsel masa kini berukuran tiga sampai delapan megabyte, sementara form
            unggah pendaftaran umumnya membatasi di angka ratusan kilobyte. Selisihnya besar, dan
            itulah kenapa hampir semua orang perlu mengompres sebelum mengunggah.
          </p>
          <p>
            Alat ini menurunkan ukuran dengan dua cara berurutan. Pertama, tingkat kualitas JPEG
            diturunkan secara bertahap sampai menemukan angka tertinggi yang hasilnya masih di bawah
            target. Bila itu belum cukup, resolusi gambar ikut dikecilkan — tapi tidak pernah sampai
            di bawah batas yang masih terbaca. Bila target tetap tidak tercapai, hasilnya dilaporkan
            sebagai tidak tercapai lengkap dengan ukuran terbaik yang berhasil dicapai.
          </p>
          <p>
            Yang tidak bisa diperbaiki oleh kompresi adalah isi fotonya. Latar belakang yang salah
            warna, foto miring, wajah terpotong, atau swafoto yang dipakai sebagai pas foto akan
            tetap ditolak verifikator berapa pun ukuran berkasnya. Perbaiki dulu fotonya, baru
            turunkan ukurannya.
          </p>
        </CaProsa>
      </div>
    </section>

    <!-- AJAKAN BERKASKILAT + KOMPRES BANYAK -->
    <section class="ca-tunda pb-12 sm:pb-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 flex flex-col gap-4">
        <RinganAjakanKompresBanyak />
        <RinganAjakanKilat />
      </div>
    </section>

    <!-- FAQ -->
    <section class="ca-tunda py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganFaq :daftar="faq" badge="Sebelum mengunggah" />
      </div>
    </section>

    <!-- ALAT LAIN -->
    <section class="ca-tunda py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganJudulBagian :level="3" judul="Alat lain" />
        <div class="mt-5 flex flex-col sm:flex-row gap-3">
          <RinganTombol variant="secondary" size="lg" icon="file-text" to="/kompres-pdf">
            Kompres PDF ke ukuran KB
          </RinganTombol>
          <RinganTombol variant="secondary" size="lg" icon="contact" to="/rapikontak">
            RapiKontak
          </RinganTombol>
        </div>
      </div>
    </section>
  </div>
</template>
